/**
 * Invite request form.
 *
 * One free-text reason, matching what the console shows an administrator. The
 * reason is optional: forcing a justification for a beta invite only produces
 * noise, and the server caps its length anyway.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { LIMITS } from '@shared/constants'
import type { InviteApplicationDto } from '@shared/types'

import { ErrorAlert } from '../layout/states'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Field, FieldGroup } from '../ui/field'
import { Textarea } from '../ui/input'
import { useApplyInvite } from '../../hooks/use-invite'
import { mutationErrorText } from '../../lib/mutation-error'
import { RejectionReason } from './panels'

export function ApplyForm({ lastApplication }: { lastApplication: InviteApplicationDto | null }) {
  const { t } = useTranslation()
  const apply = useApplyInvite()
  const [reason, setReason] = useState('')

  // The failure is read as text rather than as an `ApiError`: a request that never
  // reached the server has no `ApiError` shape but still needs a message.
  const applyError = mutationErrorText(apply.error, t)

  function submit() {
    const value = reason.trim()
    apply.mutate({ reason: value.length > 0 ? value : null }, { onSuccess: () => setReason('') })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('invite.apply.title')}</CardTitle>
        <CardDescription>{t('invite.apply.body')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {/* A previous rejection stays visible while re-applying. */}
        {lastApplication ? <RejectionReason application={lastApplication} /> : null}

        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            submit()
          }}
        >
          <FieldGroup>
            <Field
              label={t('invite.apply.reasonLabel')}
              htmlFor="invite-reason"
              hint={t('invite.apply.reasonHint')}
            >
              <Textarea
                id="invite-reason"
                name="reason"
                rows={3}
                maxLength={LIMITS.applicationReasonMaxLength}
                value={reason}
                aria-invalid={Boolean(applyError)}
                onChange={(event) => setReason(event.target.value)}
              />
            </Field>
          </FieldGroup>

          {applyError ? (
            <ErrorAlert message={applyError} />
          ) : null}

          <Button type="submit" className="self-start" disabled={apply.isPending}>
            {apply.isPending ? t('invite.apply.submitting') : t('invite.apply.submit')}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}
